// Stealth Clock — org-admin READ-ONLY shift timeline PANEL.
//
// The single implementation shared by both surfaces (mirrors the Employee Activity
// Panel/Modal/Page split):
//   ShiftTimelineDrawer — slide-over from the dashboard button (primary entry point)
//   ShiftTimeline       — standalone page at /dashboard/shift-timeline (direct-URL fallback)
//
// ADMIN ONLY. The button and route are gated client-side by canUserAccessShiftTimeline,
// and the endpoint independently enforces admin role + org.stealthClockEnabled, scoping
// every query to the caller's own organization. An employee hitting it gets 403.
//
// Read-only by design: there is no edit path in v1, because a mutable attendance record
// needs an audit trail this module does not have. The day, its boundaries and all totals
// are computed server-side in the ORGANIZATION's timezone.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { useDashTheme } from '../../pages/DashboardV2/hooks/useDashTheme';
import AntDatePicker from '../../components/UI/AntDatePicker';
import { getClockTimeline } from './api';
import { fmtTime, fmtDuration, fmtDayLabel, tzLabel, todayInOrgTz, todayLocalISO } from './timeFmt';

// Default visible rail. Extends automatically when someone works outside it (night shift).
const DEFAULT_RAIL_START_MIN = 6 * 60;
const DEFAULT_RAIL_END_MIN = 22 * 60;

export function ShiftTimelinePanel({ active = true, compact = false }) {
  const t = useDashTheme();

  // Empty = "the organization's today", resolved server-side. Deliberately NOT the
  // viewer's today: when the viewer and the org sit on opposite sides of midnight,
  // sending a local date would request the wrong day.
  const [date, setDate] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getClockTimeline({
        ...(date ? { date } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
      });
      if (!mountedRef.current) return;
      setData(res);
    } catch (err) {
      if (!mountedRef.current) return;
      const code = err?.response?.data?.code;
      setError(
        code === 'FEATURE_DISABLED'
          ? 'The shift clock is not enabled for this organization.'
          : err?.response?.data?.error || 'Could not load the shift timeline.'
      );
      if (!opts.keepData) setData(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [date, q]);

  // Only fetches while actually visible — a closed drawer costs nothing.
  // Date changes reload immediately; search is debounced so typing doesn't hammer it.
  useEffect(() => {
    if (!active) return undefined;
    const id = setTimeout(() => { load({ keepData: true }); }, q ? 350 : 0);
    return () => clearTimeout(id);
  }, [active, load, q]);

  const dayStartMs = data?.day_start ? new Date(data.day_start).getTime() : 0;
  const members = useMemo(() => data?.members || [], [data]);

  // Keep on-duty rows live. 30s (not 1s) is deliberate: this view can hold a whole
  // team, minute resolution is all it shows, and the interval only exists while ALL
  // of these hold — someone is on duty, the panel is open, and the tab is visible.
  // A closed drawer or a background tab does no work at all.
  const anyOnDuty = members.some((m) => m.state === 'in');
  const [, setTick] = useState(0);
  const [visible, setVisible] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  useEffect(() => {
    if (!anyOnDuty || !active || !visible) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [anyOnDuty, active, visible]);

  // Rail bounds: the default window, widened to fit any segment that falls outside it.
  const [railStart, railEnd] = useMemo(() => {
    let lo = DEFAULT_RAIL_START_MIN;
    let hi = DEFAULT_RAIL_END_MIN;
    for (const m of members) {
      for (const s of m.segments || []) {
        const from = (new Date(s.from).getTime() - dayStartMs) / 60000;
        const to = (new Date(s.to).getTime() - dayStartMs) / 60000;
        if (from < lo) lo = Math.floor(from / 60) * 60;
        if (to > hi) hi = Math.ceil(to / 60) * 60;
      }
    }
    return [Math.max(0, lo), Math.min(24 * 60, hi)];
  }, [members, dayStartMs]);

  const pct = useCallback(
    (ms) => {
      const min = (ms - dayStartMs) / 60000;
      const span = railEnd - railStart || 1;
      return ((Math.min(Math.max(min, railStart), railEnd) - railStart) / span) * 100;
    },
    [dayStartMs, railStart, railEnd]
  );

  const ticks = useMemo(() => {
    const out = [];
    const wide = railEnd - railStart > 14 * 60;
    const step = compact ? (wide ? 240 : 180) : wide ? 180 : 120;
    for (let m = railStart; m <= railEnd; m += step) out.push(m);
    return out;
  }, [railStart, railEnd, compact]);

  const stats = data?.stats;
  const dayEndMs = data?.day_end ? new Date(data.day_end).getTime() : Date.now();

  return (
    <div>
      {/* ── Controls ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: t.s[2],
          flexWrap: 'wrap',
          marginBottom: t.s[3],
        }}
      >
        <div style={{ position: 'relative', flex: compact ? '1 1 100%' : '0 0 auto' }}>
          <Search
            size={14}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.c.textTertiary }}
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search member"
            style={{
              background: t.c.surface,
              border: `1px solid ${t.c.border}`,
              borderRadius: t.r.md,
              color: t.c.text,
              fontSize: t.font.size.base,
              fontFamily: 'inherit',
              padding: '0 10px 0 30px',
              outline: 'none',
              width: compact ? '100%' : 180,
              // Matches AntDatePicker's controlHeight (40) so the row lines up.
              height: 40,
              boxSizing: 'border-box',
            }}
          />
        </div>
        {/* Ant date picker (the app's shared AntDatePicker — theme-aware, same control
            used elsewhere in TG). We take the FORMATTED string from onChange, not the
            Date object: the string is already the calendar day the admin picked, whereas
            converting a Date would reintroduce the viewer's timezone into a request the
            server resolves in the ORG's timezone. Clearing it returns to "org today". */}
        <AntDatePicker
          value={date || data?.date || null}
          onChange={(_date, dateString) => setDate(dateString || '')}
          format="YYYY-MM-DD"
          placeholder="Today"
          allowClear
          // Scoped override for the global `.ant-picker .ant-picker-input input`
          // 60px right-padding (see index.css). Same technique as .crm-filter-picker.
          className="stealth-clock-picker"
          // The popup portals to document.body at Ant's default z-index (~1050), which
          // is BEHIND the drawer (9999) — the calendar opened but was invisible and the
          // click landed on the drawer backdrop, closing it. Lift it above the drawer.
          // Width values are repeated because this prop replaces the base component's
          // own popupStyle rather than merging with it.
          popupStyle={{ width: 'fit-content', minWidth: 290, zIndex: 10050 }}
          // Compared as plain YYYY-MM-DD strings against the ORG's today, so a viewer
          // ahead of/behind the org can't be offered (or denied) the wrong day.
          disabledDate={(cur) => {
            if (!cur) return false;
            const maxStr = data?.tz ? todayInOrgTz(data.tz) : todayLocalISO();
            return cur.format('YYYY-MM-DD') > maxStr;
          }}
          style={{ width: compact ? 150 : 168 }}
        />
        <button
          type="button"
          onClick={() => load({ keepData: true })}
          disabled={loading}
          title="Refresh"
          style={{
            background: t.c.surface,
            border: `1px solid ${t.c.border}`,
            borderRadius: t.r.md,
            color: t.c.textSecondary,
            width: 40,
            height: 40,
            padding: 0,
            cursor: loading ? 'default' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
        {data?.date && (
          <span style={{ marginLeft: 'auto', fontSize: t.font.size.sm, color: t.c.textTertiary }}>
            {fmtDayLabel(data.date)} · all times {tzLabel(data.tz)}
          </span>
        )}
      </div>

      {/* ── Stat chips ───────────────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'flex', gap: t.s[2], flexWrap: 'wrap', marginBottom: t.s[4] }}>
          <Chip t={t} label="Members" value={stats.total} />
          <Chip t={t} label="On duty" value={stats.on_duty} tone={t.status.positive} bg={t.status.positiveSoft} />
          <Chip t={t} label="Recorded" value={stats.recorded} />
          <Chip t={t} label="No record" value={stats.no_record} tone={t.c.textTertiary} />
          {stats.anomalies > 0 && (
            <Chip t={t} label="Needs review" value={stats.anomalies} tone={t.status.warning} bg={t.status.warningSoft} />
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: t.s[2],
            padding: t.s[4],
            borderRadius: t.r.md,
            background: t.status.negativeSoft,
            color: t.status.negative,
            fontSize: t.font.size.base,
            marginBottom: t.s[4],
          }}
        >
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      {/* ── Rows ─────────────────────────────────────────────────────────────── */}
      <div
        style={{
          background: t.c.surface,
          border: `1px solid ${t.c.border}`,
          borderRadius: t.r.lg,
          boxShadow: compact ? 'none' : t.shadow.sm,
          overflow: 'hidden',
        }}
      >
        {loading && !data ? (
          <div style={{ padding: t.s[8], textAlign: 'center', color: t.c.textTertiary, fontSize: t.font.size.base }}>
            Loading…
          </div>
        ) : members.length === 0 ? (
          <div style={{ padding: t.s[8], textAlign: 'center', color: t.c.textTertiary, fontSize: t.font.size.base }}>
            {error ? 'Nothing to show.' : 'No stealth-agent members found for this organization.'}
          </div>
        ) : (
          members.map((m, i) => (
            <MemberRow
              key={m.user_id}
              t={t}
              m={m}
              tz={data.tz}
              pct={pct}
              ticks={ticks}
              railStart={railStart}
              railEnd={railEnd}
              dayStartMs={dayStartMs}
              dayEndMs={dayEndMs}
              compact={compact}
              last={i === members.length - 1}
            />
          ))
        )}
      </div>

      <p style={{ marginTop: t.s[3], fontSize: t.font.size.xs, color: t.c.textTertiary, lineHeight: 1.7 }}>
        Shift markers only. They do not start, stop or change background tracking, and are
        not used in any hours figure elsewhere in TeamGrid.
      </p>
    </div>
  );
}

function Chip({ t, label, value, tone, bg }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 6,
        padding: '5px 11px',
        borderRadius: t.r.pill,
        background: bg || t.c.surfaceMuted,
        color: tone || t.c.textSecondary,
        fontSize: t.font.size.sm,
      }}
    >
      <strong style={{ fontWeight: t.font.weight.semibold, fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
      {label}
    </span>
  );
}

function MemberRow({ t, m, tz, pct, ticks, railStart, railEnd, dayStartMs, dayEndMs, compact, last }) {
  const initials =
    (m.name || m.email || '?')
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  const hue = t.accents[Math.abs(hashCode(m.user_id)) % t.accents.length];
  const staleOpen = m.stale_open === true;
  const hasAny = (m.segments || []).length > 0 || (m.anomalies || []).length > 0 || staleOpen;

  // SetupProfile.designation sometimes holds the TG ROLE ("org_user") rather than a job
  // title. Showing "org_user" under someone's name is noise, so fall back to their email.
  const ROLE_LIKE = /^(org_user|org_admin|main_admin|user|admin|manager|employee)$/i;
  const subtitle =
    (m.designation && !ROLE_LIKE.test(m.designation.trim()) && m.designation) || m.email || '';

  // An open segment keeps growing after the response was built, so its end and the
  // day total are recomputed client-side against "now" (bounded by the day's end, so
  // a past day never ticks). Closed segments always use the server's values.
  const liveEnd = (s) => (s.open ? Math.min(Date.now(), dayEndMs) : new Date(s.to).getTime());
  const liveSec = (s) =>
    s.open ? Math.max(0, Math.floor((liveEnd(s) - new Date(s.from).getTime()) / 1000)) : s.sec;
  const liveTotalSec = staleOpen
    ? 0
    : (m.segments || []).reduce((sum, s) => sum + liveSec(s), 0);

  // An open segment means two very different things depending on the day being viewed:
  // on TODAY the person is still working; on a PAST day they simply never clocked out.
  // Both are the same stored data — only the label differs.
  const dayIsCurrent = dayEndMs > Date.now();

  return (
    <div
      style={{
        padding: `${t.s[4]}px ${compact ? t.s[4] : t.s[5]}px`,
        borderBottom: last ? 'none' : `1px solid ${t.c.border}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: t.s[3], marginBottom: t.s[3] }}>
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            flex: 'none',
            display: 'grid',
            placeItems: 'center',
            background: hue,
            color: '#fff',
            fontSize: t.font.size.sm,
            fontWeight: t.font.weight.semibold,
          }}
        >
          {initials}
        </span>
        {/* flex:1 + minWidth:0 lets the name ellipsis instead of pushing the total out
            of the row on a narrow drawer. */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            title={m.name || m.email || m.user_id}
            style={{
              fontSize: t.font.size.base,
              fontWeight: t.font.weight.semibold,
              color: t.c.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {m.name || m.email || m.user_id}
          </div>
          {subtitle && (
            <div
              title={subtitle}
              style={{
                fontSize: t.font.size.sm,
                color: t.c.textTertiary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {m.state === 'in' && (
          <span
            style={{
              marginLeft: t.s[2],
              fontSize: t.font.size.xs,
              fontWeight: t.font.weight.semibold,
              color: t.status.positive,
              background: t.status.positiveSoft,
              padding: '3px 8px',
              borderRadius: t.r.pill,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            On duty
          </span>
        )}
        {/* Never shrinks — the total is the one thing that must stay readable. */}
        <div style={{ marginLeft: 'auto', textAlign: 'right', flexShrink: 0 }}>
          <div
            style={{
              fontSize: t.font.size.md,
              fontWeight: t.font.weight.semibold,
              fontVariantNumeric: 'tabular-nums',
              color: hasAny ? t.c.text : t.c.textTertiary,
              whiteSpace: 'nowrap',
            }}
          >
            {staleOpen || !hasAny ? '—' : fmtDuration(liveTotalSec)}
          </div>
          <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, whiteSpace: 'nowrap' }}>
            recorded
          </div>
        </div>
      </div>

      {/* Rail */}
      <div style={{ position: 'relative', height: 34 }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 9,
            height: 16,
            background: t.c.surfaceMuted,
            borderRadius: 6,
          }}
        />
        {(m.segments || []).map((s, i) => {
          const left = pct(new Date(s.from).getTime());
          // Open segments keep growing — measure to "now", not to the stale response.
          const width = Math.max(pct(liveEnd(s)) - left, 0.7);
          return (
            <motion.div
              key={`${s.from}-${i}`}
              initial={{ opacity: 0, scaleX: 0.85 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ ...t.motion.spring.snappy, delay: i * 0.03 }}
              title={
                s.open
                  ? dayIsCurrent
                    ? `${fmtTime(s.from, tz)} → running (${fmtDuration(liveSec(s))} so far)`
                    : `${fmtTime(s.from, tz)} → never clocked out`
                  : `${fmtTime(s.from, tz)} → ${fmtTime(s.to, tz)} (${fmtDuration(s.sec)})`
              }
              style={{
                position: 'absolute',
                top: 9,
                height: 16,
                left: `${left}%`,
                width: `${width}%`,
                borderRadius: 6,
                transformOrigin: 'left center',
                background: s.open
                  ? `repeating-linear-gradient(135deg, ${t.status.warning} 0 7px, ${t.status.warningSoft} 7px 14px)`
                  : t.c.primaryGradient,
                boxShadow: s.open ? 'none' : `0 1px 3px ${t.c.primaryRing}`,
              }}
            />
          );
        })}
      </div>

      {/* Ticks. Edge labels are anchored to the edge, not centred on it — a centred
          label at 0% / 100% hangs half outside the container and gets clipped
          (":00 am" on the left, a wrapped "10:00 pm" on the right). */}
      <div style={{ position: 'relative', height: 16 }}>
        {ticks.map((min) => {
          const isFirst = min === ticks[0];
          const isLast = min === ticks[ticks.length - 1];
          return (
            <span
              key={min}
              style={{
                position: 'absolute',
                top: 0,
                left: `${((min - railStart) / (railEnd - railStart || 1)) * 100}%`,
                transform: isFirst ? 'translateX(0)' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                fontSize: t.font.size.xs,
                color: t.c.textTertiary,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap', // never wrap "10:00 pm" onto a second line
              }}
            >
              {fmtTime(new Date(dayStartMs + min * 60000), tz)}
            </span>
          );
        })}
      </div>

      {/* Exact times */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: t.s[2] }}>
        {(m.segments || []).length === 0 && (m.anomalies || []).length === 0 ? (
          <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>No shift recorded</span>
        ) : (
          <>
            {(m.segments || []).map((s, i) => (
              <span
                key={`c-${i}`}
                style={{
                  fontSize: t.font.size.sm,
                  fontVariantNumeric: 'tabular-nums',
                  padding: '3px 9px',
                  borderRadius: t.r.pill,
                  background: s.open ? t.status.warningSoft : t.c.surfaceMuted,
                  color: s.open ? t.status.warning : t.c.textSecondary,
                  fontWeight: s.open ? t.font.weight.semibold : t.font.weight.regular,
                }}
              >
                {s.clipped_start ? '⤷ ' : ''}
                {fmtTime(s.from, tz)} →{' '}
                {s.open
                  ? dayIsCurrent
                    ? `running · ${fmtDuration(liveSec(s))}`
                    : 'no clock-out'
                  : `${fmtTime(s.to, tz)} · ${fmtDuration(s.sec)}`}
                {s.clipped_end ? ' ⤶' : ''}
              </span>
            ))}
            {(m.anomalies || []).map((a, i) => (
              <span
                key={`a-${i}`}
                title={
                  a.type === 'orphan_out'
                    ? 'Clock-out with no matching clock-in — not counted'
                    : 'Duplicate clock-in — ignored'
                }
                style={{
                  fontSize: t.font.size.sm,
                  padding: '3px 9px',
                  borderRadius: t.r.pill,
                  background: t.status.warningSoft,
                  color: t.status.warning,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <AlertTriangle size={11} />
                {a.type === 'orphan_out' ? 'stray clock-out' : 'duplicate start'} {fmtTime(a.at, tz)}
              </span>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < String(str).length; i += 1) {
    h = (h << 5) - h + String(str).charCodeAt(i);
    h |= 0;
  }
  return h;
}

export default ShiftTimelinePanel;
